import { useState } from 'react';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  SimpleGrid,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { djs } from '@duke-hq/djs';
import {
  FormContentContainer,
  FormFooterContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { testIds } from '../testIds';
import { EmployerDocUploadField as DocUploadField } from '../components/EmployerDocUploadField';
import type { PlacementsDetails } from './types';

export function EmployerPlacements({
  placements,
  setPlacements,
  goToPrevStep,
  goToNextStep,
}: {
  placements: PlacementsDetails;
  setPlacements: (placements: PlacementsDetails) => void;
  goToPrevStep: () => void;
  goToNextStep: () => void;
}): React.ReactElement {
  const update = (patch: Partial<PlacementsDetails>) => setPlacements({ ...placements, ...patch });

  const [showErrors, setShowErrors] = useState(false);

  const today = djs().format('YYYY-MM-DD');

  const canProceed =
    !placements.readyForPlacements ||
    (placements.agreement != null &&
      placements.agreementExpiresAt >= today &&
      placements.insurance != null &&
      placements.insuranceExpiresAt >= today);

  const handleNext = () => {
    if (canProceed) {
      goToNextStep();
      return;
    }
    setShowErrors(true);
  };

  return (
    <>
      <FormContentContainer>
        <VStack align="stretch" spacing={4} w="full" pb={4}>
          <Text fontWeight="semibold">
            <Trans>Is this employer ready to accept student placements?</Trans>
          </Text>
          <Text fontSize="sm" color="fg.subtle">
            <Trans>
              All businesses must have a signed agreement and provided a current certificate of
              insurance before accepting student placements
            </Trans>
          </Text>

          <Switch
            colorScheme="brand"
            isChecked={placements.readyForPlacements}
            onChange={(event) => update({ readyForPlacements: event.target.checked })}
            data-testid={testIds.readyForPlacementsToggle}
            display="flex"
            alignItems="center"
          >
            <Trans>Ready for placements</Trans>
          </Switch>

          {placements.readyForPlacements ? (
            <VStack align="stretch" spacing={4} w="full">
              <Text fontSize="sm" color="fg.subtle">
                <Trans>Please upload the required documents</Trans>
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={4} spacingY={4}>
                <DocUploadField
                  label={<Trans>Upload agreement</Trans>}
                  kind="EMPLOYER_AGREEMENT"
                  doc={placements.agreement}
                  onUploaded={(doc) => update({ agreement: doc })}
                  onClear={() => update({ agreement: null })}
                  isInvalid={showErrors && placements.agreement == null}
                  errorMessage={<Trans>Upload the signed agreement</Trans>}
                  data-testid={testIds.agreementUpload}
                />
                <FormControl
                  isRequired
                  isInvalid={showErrors && placements.agreementExpiresAt < today}
                >
                  <FormLabel>
                    <Trans>Agreement expiry</Trans>
                  </FormLabel>
                  <Input
                    type="date"
                    min={today}
                    value={placements.agreementExpiresAt}
                    onChange={(event) => update({ agreementExpiresAt: event.target.value })}
                    data-testid={testIds.agreementExpiryInput}
                  />
                  <FormErrorMessage>
                    <Trans>Set an agreement expiry date of today or later</Trans>
                  </FormErrorMessage>
                </FormControl>

                <DocUploadField
                  label={<Trans>Upload certificate of insurance</Trans>}
                  kind="EMPLOYER_INSURANCE"
                  doc={placements.insurance}
                  onUploaded={(doc) => update({ insurance: doc })}
                  onClear={() => update({ insurance: null })}
                  isInvalid={showErrors && placements.insurance == null}
                  errorMessage={<Trans>Upload the certificate of insurance</Trans>}
                  data-testid={testIds.insuranceUpload}
                />
                <FormControl
                  isRequired
                  isInvalid={showErrors && placements.insuranceExpiresAt < today}
                >
                  <FormLabel>
                    <Trans>Insurance expiry</Trans>
                  </FormLabel>
                  <Input
                    type="date"
                    min={today}
                    value={placements.insuranceExpiresAt}
                    onChange={(event) => update({ insuranceExpiresAt: event.target.value })}
                    data-testid={testIds.insuranceExpiryInput}
                  />
                  <FormErrorMessage>
                    <Trans>Set an insurance expiry date of today or later</Trans>
                  </FormErrorMessage>
                </FormControl>
              </SimpleGrid>
            </VStack>
          ) : null}
        </VStack>
      </FormContentContainer>

      <FormFooterContainer>
        <Button
          variant="primary"
          rightIcon={<ChevronRight />}
          onClick={handleNext}
          data-testid={testIds.step2NextButton}
        >
          <Trans>Next</Trans>
        </Button>
        <Button variant="secondary" leftIcon={<ChevronLeft />} onClick={goToPrevStep}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </>
  );
}
